import { browserSessionPersistence, setPersistence } from "firebase/auth";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  pushProviderToGlobalDataLayer,
  pushProviderUserIdToGlobalDataLayer
} from "services/firebase/auth/analytics/events";
import { ProviderSSO } from "services/firebase/auth/types";

import { isSignedIn } from "seneca-common/features/user/state";
import { useTypedSearchParams } from "seneca-common/utils/routing/react-router-typesafe-routes/dom";
import logError from "seneca-common/utils/sentry/logError";

import { getRegion } from "features/regions/selectors";
import useSignUpJourneyStateOnMount from "features/sign-up/global-sign-up-modal/hooks/useSignUpJourneyStateOnMount";
import { useRedirectOnSignInCallback } from "features/sign-up/hooks";
import { signOut } from "features/user";
import { proceedUserSignIn } from "features/user/actions/auth/sso/utils";
import { ROUTES } from "routing/routes";

import { setHideImpersonationBanner } from "./hideImpersonationBanner";

export default function useAuthWithCustomToken() {
  const [{ customAccessToken: token, hideCustomTokenBanner: hideBanner }] =
    useTypedSearchParams(ROUTES);

  const dispatch = useDispatch();
  const region = useSelector(getRegion)!;
  const { redirectAuthenticatedUserTo } = useSignUpJourneyStateOnMount();
  const onSignInSuccess = useRedirectOnSignInCallback(
    redirectAuthenticatedUserTo
  );
  const signedIn = useSelector(isSignedIn);

  useEffect(() => {
    setHideImpersonationBanner(!!hideBanner);

    (async () => {
      if (token) {
        try {
          const imports = await lazyLoadImports();
          const { signInWithCustomToken, getFirebaseAuth } = imports;

          const auth = getFirebaseAuth();

          if (!hideBanner) {
            await dispatch(signOut());
          }

          await setPersistence(auth, browserSessionPersistence);

          const { user } = await signInWithCustomToken(auth, token);

          pushProviderToGlobalDataLayer(
            user.providerData[0].providerId as ProviderSSO
          );
          pushProviderUserIdToGlobalDataLayer(user.providerData[0].uid);

          const signInData = {
            region,
            userId: user.uid,
            onSignInSuccess
          };

          await dispatch(proceedUserSignIn(signInData));

          signedIn && window.location.reload();
        } catch (error) {
          logError(error, { message: "Failed to auth with custom token" });
        }
      }
    })();
  }, [signedIn, token, region, dispatch, onSignInSuccess, hideBanner]);
}

async function lazyLoadImports() {
  const [{ signInWithCustomToken }, { getFirebaseAuth }] = await Promise.all([
    import("firebase/auth"),
    import("services/firebase/auth")
  ]);

  return { signInWithCustomToken, getFirebaseAuth };
}
